import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import GlobalWrapper from "../../../../components/Wrapper/GlobalWrapper";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import {
  assignleadtoOGA,
  getAllOGAs,
  getLeadsUnderOGA,
  getOGAunderSOGO,
} from "../../../../redux/features/ogdSlice";
import { useFormik } from "formik";
import { Icon } from "@iconify/react/dist/iconify.js";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

const AssignLeads = () => {
  const dispatch = useDispatch();
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { loading, ogaunderSogo, AllogaList } = useSelector(
    (state) => state.ogd
  );
  const { enqueueSnackbar } = useSnackbar();

  const [leads, setLeads] = useState([]);

  // ✅ Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ogaIds: [],
      leads: [],
      newogaID: null,
    },
    validationSchema: Yup.object().shape({
      ogaIds: Yup.array().min(1, "Select at least one OGA"),
      leads: Yup.array().min(1, "Select at least one lead"),
      newogaID: Yup.object().nullable().required("Select new OGA"),
    }),
    onSubmit: (values) => {
      const payload = {
        leadIds: values.leads.map((item) => item.lead_id),
        ogaId: values.newogaID?.userID || null,
      };

      dispatch(assignleadtoOGA(payload)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          formik.resetForm();
        } else {
          enqueueSnackbar(res.payload.message, { variant: "error" });
        }
      });
    },
  });

  useEffect(() => {
    dispatch(getOGAunderSOGO());
    dispatch(getAllOGAs());
  }, [dispatch]);

  useEffect(() => {
    if (formik.values.ogaIds.length > 0) {
      const ogaIds = formik.values.ogaIds.map((b) => b.userID);
      dispatch(getLeadsUnderOGA({ ogaIds })).then((res) => {
        if (res.payload?.status === "success") {
          setLeads(res.payload.data || []);
        } else {
          setLeads([]);
        }
      });
    } else {
      setLeads([]);
    }
  }, [formik.values.ogaIds, dispatch]);

  return (
    <GlobalWrapper title={"OGD Migration - Leads Reassignment"}>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ width: "100%" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3} sm={3}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={ogaunderSogo}
              getOptionLabel={(option) => option.finalName || ""}
              value={formik.values.ogaIds}
              onChange={(e, value) => formik.setFieldValue("ogaIds", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose OGA(s)"
                  placeholder="Choose OGA(s)"
                  error={formik.touched.ogaIds && Boolean(formik.errors.ogaIds)}
                  helperText={formik.touched.ogaIds && formik.errors.ogaIds}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} sm={3}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={leads.length === 0}
              options={[
                {
                  lead_id: "ALL",
                  lead_name: "Select All Students",
                  lead_mob_no: "",
                },
                ...leads,
              ]}
              getOptionLabel={(option) =>
                option.lead_id === "ALL"
                  ? option.lead_name
                  : `${option.lead_name} (${option.lead_mob_no})`
              }
              value={formik.values.leads}
              onChange={(e, value) => {
                if (value.some((v) => v.lead_id === "ALL")) {
                  if (formik.values.leads.length === leads.length) {
                    formik.setFieldValue("leads", []);
                  } else {
                    formik.setFieldValue("leads", leads);
                  }
                } else {
                  formik.setFieldValue("leads", value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    loading
                      ? "Loading Data"
                      : leads.length === 0 && formik.values.ogaIds.length > 0
                      ? "No Active Students"
                      : "Select Students"
                  }
                  placeholder="Select Students"
                  error={formik.touched.leads && Boolean(formik.errors.leads)}
                  helperText={formik.touched.leads && formik.errors.leads}
                />
              )}
              renderTags={
                leads.length === formik.values.leads.length
                  ? () => {
                      return (
                        <Chip
                          label="All Students Selected"
                          color="primary"
                          variant="filled"
                          icon={
                            <Icon icon="mdi:check-all" width={20} height={20} />
                          }
                        />
                      );
                    }
                  : null
              }
            />
          </Grid>
          <Grid item xs={12} md={3} sm={3}>
            <Autocomplete
              options={AllogaList}
              getOptionLabel={(option) => option.finalName || ""}
              value={formik.values.newogaID}
              onChange={(e, value) => formik.setFieldValue("newogaID", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose New OGA"
                  placeholder="Choose New OGA"
                  error={
                    formik.touched.newogaID && Boolean(formik.errors.newogaID)
                  }
                  helperText={formik.touched.newogaID && formik.errors.newogaID}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} sm={3}>
            <Button
              variant="contained"
              startIcon={
                <Icon icon="lsicon:submit-filled" width={20} height={20} />
              }
              type="submit"
              color="secondary"
              disabled={loading && formik.isSubmitting}
              fullWidth
              size="large"
            >
              {loading && formik.isSubmitting
                ? "Reassigning..."
                : "Reassign Leads"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </GlobalWrapper>
  );
};

export default AssignLeads;
